import { createSlice } from "@reduxjs/toolkit";

/**
 * MLD (Multi-tech) Report Slice
 *
 * Mirrors the pattern from ReportSlice.js used by individual tech reports (UF/IX/RO).
 *
 * State shape:
 * ─────────────────────────────────────────────────────────────────
 * mldCalcReportApiResp   Raw API response state { isLoading, data, error }
 *                        data holds the unformatted response from /MultitechReport.
 *
 * mldReportTableData     Formatted table data keyed by canvas nodeId.
 *                        Each entry holds the tech-specific table sections
 *                        after the response has been parsed and transformed
 *                        (mirrors ufReportTableData / ixReportTableData / roReportTableData).
 *                        Shape: { [nodeId]: { <section_key>: <table_rows[]> } }
 *
 * ─────────────────────────────────────────────────────────────────
 * Formatting convention (matches existing per-tech hooks):
 *   1. Raw response lands in mldCalcReportApiResp.data  (via setMldCalcReportApiResp)
 *   2. Each tech-node's data is parsed by its use[Tech]Report hook
 *      and stored per-nodeId in mldReportTableData  (via setMldReportTableData)
 */

const DEFAULT_API_RESP = {
  isLoading: false,
  data: null,
  error: null,
};

const MLD_COMMON_SECTION_DEFAULTS = {
  mldCommonSectionData: {
    mld_summary_diagram: true,
    mld_flow_overview: {
      flow_by_technology_table: true,
      costs_by_technology: true,
      stream_table: true,
    },
  },
};

const DEFAULT_NODE_SECTION_DATA = {
  UF: {
    ufSectionData: {
      sytem_diagram: true,
      uf_system_overview: true,
      uf_operating_conditions: true,
      uf_water_quality: true,
      uf_design_quality: true,
    },
  },
  IX: {
    ixSectionData: {
      sytem_diagram: true,
      ix_system_overview: true,
      ix_resins_and_vessels: true,
      ix_water_quality: true,
      ix_design_warnings: true,
    },
  },
  RO: {
    roSectionData: {
      sytem_diagram: true,
      ro_design_diagram: true,
      ro_system_overview: true,
      ro_pass_summary: true,
      ro_design_warnings: true,
      ro_special_comments: true,
    },
  },
  CCRO: {
    ccroSectionData: {
      sytem_diagram: true,
      ccro_design_diagram: true,
      ccro_system_overview: true,
      ccro_pass_summary: true,
      ccro_design_warnings: true,
      ccro_special_comments: true,
      ccro_overview: true,
    },
  },
  ADJUSTMENT: {
    phAdjSectionData: {
      ph_adj_water_quality: true,
      ph_adj_chemical: true,
    },
  },
  WQA_ADJUSTMENT: {
    wqaSectionData: {
      wqa_water_quality: true,
    },
  },
  MIX: {
    mixSectionData: {
      mix_blended_table: true,
    },
  },
};

const initialState = {
  /** Temperature selected in the report sidebar (persists across report navigation). */
  reportTemperature: null,

  /** Base64-encoded report images keyed by section (e.g. "processFlow", "design"). */
  reportImages: {},

  /** Raw multi-tech calc report API response — single combined slot for the whole MLD response. */
  mldCalcReportApiResp: DEFAULT_API_RESP,

  /**
   * Formatted common report table data shared across all nodes.
   * Keys: project_info, additional_water_info, blended_feed_streams, blended_feed_additional_water_info.
   */
  commonReportTableData: {},

  /**
   * Per-node side panel section selection data.
   * Shape: { [nodeId]: { [sectionId]: { [itemKey]: boolean } } }
   * Example: { "ro1": { roSectionData: { sytem_diagram: true, ro_system_overview: true } } }
   */
  mldNodeSectionData: {},

  /**
   * Formatted table data keyed by API node ID (e.g. "ro1", "uf1", "ix1").
   * Each value is the raw formatted response for that node, mirroring the
   * shape stored in ufCalcReportApiResp.data / roCalcReportApiResp.data etc.
   *
   * Example:
   * {
   *   "ro1": { roSystem: { passOverview: [...], ... } },
   *   "uf1": { uf_system_overview: {...}, uf_water_quality: {...}, ... },
   * }
   */
  mldReportTableData: {},

  /**
   * Ordered list of treatment API IDs, derived from the canvas node order.
   * Used by WPReportMldContent to render tech sections in the correct sequence.
   * Example: ["ro1", "uf1"]
   */
  mldNodeOrder: [],

  /**
   * Metadata keyed by API node ID.
   * Shape: { [nodeId]: { techType: "RO" | "UF" | "IX", treatment: string } }
   * Example: { "ro1": { techType: "RO", treatment: "RO" }, "uf1": { techType: "UF", treatment: "UF" } }
   */
  mldNodeMeta: {},

  /**
   * Formatted table data for MLD common sections (shared across all nodes).
   * Keyed by table section id (e.g. "flow_by_technology").
   * Shape: { [sectionKey]: <table_rows[]> }
   */
  mldCommonTableData: {},
};

export const MldReportSlice = createSlice({
  name: "mldReport",
  initialState,
  reducers: {
    /**
     * Update raw API response state.
     * Accepts partial updates (same pattern as setUfCalcReportApiResp etc.).
     *
     * Usage:
     *   dispatch(setMldCalcReportApiResp({ isLoading: true, data: null, error: null }))
     *   dispatch(setMldCalcReportApiResp({ data: responseData }))
     *   dispatch(setMldCalcReportApiResp({ isLoading: false }))
     */
    setMldCalcReportApiResp: (state, action) => {
      state.mldCalcReportApiResp = { ...state.mldCalcReportApiResp, ...action.payload };
    },

    /**
     * Store raw formatted response data for a specific API node ID.
     * Merges top-level keys so individual node updates don't overwrite siblings.
     *
     * Usage:
     *   dispatch(setMldReportTableData({ nodeId: "ro1", data: { roSystem: {...} } }))
     */
    setMldReportTableData: (state, action) => {
      const { nodeId, data } = action.payload;
      state.mldReportTableData[nodeId] = {
        ...state.mldReportTableData[nodeId],
        ...data,
      };
    },

    /**
     * Set the ordered list of treatment API IDs (derived from canvas node order).
     *
     * When the list contains more than one node, the `mld_common` section defaults
     * are initialized in `mldNodeSectionData` if they have not been set yet.
     *
     * @param {import("@reduxjs/toolkit").Draft<typeof initialState>} state - Current slice state (Immer draft).
     * @param {{ payload: string[] }} action - Action whose payload is the ordered array of API node IDs.
     * @example
     * dispatch(setMldNodeOrder(["ro1", "uf1"]));
     */
    setMldNodeOrder: (state, action) => {
      state.mldNodeOrder = action.payload;
      if (action.payload.length > 1) {
        const existing = state.mldNodeSectionData["mld_common"]?.mldCommonSectionData;
        const hasDefaults = existing && Object.keys(existing).length > 0;
        if (!hasDefaults) {
          state.mldNodeSectionData["mld_common"] = MLD_COMMON_SECTION_DEFAULTS;
        }
      }
    },

    /**
     * Store metadata for a single API node ID.
     * Usage:
     *   dispatch(setMldNodeMeta({ nodeId: "ro1", techType: "RO", treatment: "RO", displayName: "RO Main" }))
     */
    setMldNodeMeta: (state, action) => {
      const { nodeId, techType, treatment, displayName, canvasNodeId, adjustmentType } = action.payload;
      state.mldNodeMeta[nodeId] = {
        techType,
        treatment,
        displayName,
        adjustmentType: adjustmentType ?? null,
        ...(canvasNodeId && { canvasNodeId }),
      };
      // Initialize default section data if not already set for this node
      if (!state.mldNodeSectionData[nodeId]) {
        const sectionKey = techType === "ADJUSTMENT" && adjustmentType === "WQA" ? "WQA_ADJUSTMENT" : techType;
        state.mldNodeSectionData[nodeId] = DEFAULT_NODE_SECTION_DATA[sectionKey] ?? {};
      }
    },

    /** Reset to initial state (e.g. on case/project change) */
    resetMldReportSlice: () => initialState,

    /**
     * Update section panel selection data for a specific node.
     * Skips no-op dispatches (e.g. the cleanup effect sending an empty object).
     * Usage:
     *   dispatch(setMldNodeSectionData({ nodeId: "ro1", id: "roSectionData", data: { sytem_diagram: true } }))
     */
    setMldNodeSectionData: (state, action) => {
      const { nodeId, id, data } = action.payload;
      if (!data || Object.keys(data).length === 0) return;
      if (!state.mldNodeSectionData[nodeId]) {
        state.mldNodeSectionData[nodeId] = {};
      }
      state.mldNodeSectionData[nodeId][id] = {
        ...state.mldNodeSectionData[nodeId][id],
        ...data,
      };
    },

    /**
     * Store formatted table data for MLD common sections.
     * Merges with existing keys so individual section updates don't overwrite siblings.
     *
     * Usage:
     *   dispatch(setMldCommonTableData({ flow_by_technology: rows }))
     */
    setMldCommonTableData: (state, action) => {
      state.mldCommonTableData = { ...state.mldCommonTableData, ...action.payload };
    },

    /** Merge report images. `processFlow` is shared; `design` is keyed by nodeId. */
    setReportImages: (state, action) => {
      const { design, ...rest } = action.payload;
      state.reportImages = { ...state.reportImages, ...rest };
      if (design !== undefined) {
        state.reportImages.design = { ...(state.reportImages.design || {}), ...design };
      }
    },

    /** Store formatted common report table data (project_info, additional_water_info, etc.). */
    setCommonReportTableData: (state, action) => {
      state.commonReportTableData = { ...state.commonReportTableData, ...action.payload };
    },
  },
});

export const {
  setMldCalcReportApiResp,
  setMldReportTableData,
  setMldNodeOrder,
  setMldNodeMeta,
  setMldNodeSectionData,
  setMldCommonTableData,
  setReportImages,
  setCommonReportTableData,
  resetMldReportSlice,
} = MldReportSlice.actions;

export default MldReportSlice.reducer;
